import { ReactNode, useCallback } from 'react';

import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import {
  ACTIVITIES_ANSWER_MODE_FREE,
  LESSON_ACTIVITIES,
} from 'global/constants';
import { getComponentSelectorId } from '../../shared/logic/get-component-selector-id';
import SubActivityView from '../../sub-activity/sub-activity-view';
import { getSubActivityInstructionAsText } from '../../sub-activity/sub-activity.utils';
import MultipleChoiceAnswerTeacher from '../multiple-choice-answer-teacher';
import MultipleChoiceCheckAnswerFreeTeacher from '../multiple-choice-check-answer-free-teacher';
import MultipleChoiceSelectedAnswers from '../multiple-choice-selected-answers';
import { ViewProps } from '../multiple-choice.constants';
import { ComponentOverlayWrapper } from '../../shared/component-overlay/component-overlay.style';
import ComponentOverlaySample from '../../shared/component-overlay/component-overlay-sample';
import { useCheckAnswer } from 'approot/activity-builder/check-answer/check-answer.hooks';
import { checkAnswerVar } from 'approot/activity-builder/check-answer/check-answer.apollo';

const MultipleChoiceViewSample = (
  props: ViewProps & { banner?: ReactNode }
) => {
  const { subActivityProperties, id, properties } = props;

  const { isVisible, openModal, closeModal } = useSimpleModalState();

  const { isZoomToFit, isPublished, contentType } =
    useActivityBuilderPresentContext();

  const isLesson = contentType === LESSON_ACTIVITIES;

  const isFreeAnswer =
    properties.multipleChoice.answerMode === ACTIVITIES_ANSWER_MODE_FREE;

  const { checkAnswerData } = useCheckAnswer({
    componentId: id,
  });

  const handleClose = useCallback(() => {
    const componentAnswers = checkAnswerData[id];
    if (componentAnswers) {
      checkAnswerVar({
        ...checkAnswerData,
        [id]: {
          ...componentAnswers,
          hasChecked: false,
          hasReset: true,
          answer: undefined,
          multiAnswer: componentAnswers.multiAnswer
            ? {
                ...componentAnswers.multiAnswer,
                answerRefs: [],
              }
            : undefined,
        },
      });
    }
    closeModal();
  }, [checkAnswerData, id, closeModal]);

  return (
    <SubActivityView subActivityDetails={subActivityProperties}>
      {isVisible && !isFreeAnswer && (
        <MultipleChoiceAnswerTeacher
          {...props}
          isPublished={isPublished}
          canClearModelledResponse={false}
          onClose={() => handleClose()}
        />
      )}

      {isVisible && isFreeAnswer && (
        <MultipleChoiceCheckAnswerFreeTeacher
          {...props}
          isPublished={isPublished}
          canClearModelledResponse={false}
          onClose={() => handleClose()}
        />
      )}

      <ComponentOverlayWrapper id={getComponentSelectorId(id)}>
        <MultipleChoiceSelectedAnswers
          {...props}
          isResponsive={!isZoomToFit}
          selectedAnswers={undefined}
          showAnswer={isLesson}
        />
        <ComponentOverlaySample
          ariaLabel={`Respond to multiple choice: ${getSubActivityInstructionAsText(
            subActivityProperties?.instructions
          )}`}
          onClick={() => openModal()}
        />
      </ComponentOverlayWrapper>
    </SubActivityView>
  );
};

export default MultipleChoiceViewSample;
